import { makeAutoObservable } from "mobx";

import { updateTeam, uploadTeamAvatar, leaveTeam } from "@/api";
import { subscribe } from "@/api/cable";
import { validateEmail } from "@/utils/validateEmail";

import type { Team, TeamShort, UpdateTeamBody, UploadTeamAvatarBody } from "@/api";
import type { Channel } from "@anycable/core";

export type TeamFormFields = "title" | "about" | "permalink";

const PERMALINK_MIN_LENGTH = 3;
const PERMALINK_MAX_LENGTH = 40;

const TITLE_MAX_LENGTH = 100;
const ABOUT_MAX_LENGTH = 1000;

const PERMALINK_REGEX = new RegExp(
  `^[A-Za-z0-9\\-_.]{${PERMALINK_MIN_LENGTH},${PERMALINK_MAX_LENGTH}}$`,
);

export function validateTeamPermalink(value: string) {
  if (value.length === 0) {
    return "createTeamDialog.stepPermalink.errors.empty";
  }
  if (value.length > PERMALINK_MAX_LENGTH) {
    return "createTeamDialog.stepPermalink.errors.tooLong";
  }
  if (value.length < PERMALINK_MIN_LENGTH) {
    return "createTeamDialog.stepPermalink.errors.tooShort";
  }
  if (!PERMALINK_REGEX.test(value)) {
    return "createTeamDialog.stepPermalink.errors.invalid";
  }
  return null;
}

export function validateTeamTitle(value: string) {
  if (value.length === 0) {
    return "createTeamDialog.stepTitle.errors.empty";
  }
  if (value.length > TITLE_MAX_LENGTH) {
    return "createTeamDialog.stepTitle.errors.tooLong";
  }
  return null;
}

export function validateTeamAbout(value: string) {
  if (value.length > ABOUT_MAX_LENGTH) {
    return "createTeamDialog.stepAbout.errors.tooLong";
  }
  return null;
}

export function validateTeamEmail(value: string) {
  if (value.length === 0) {
    return "auth.errors.email.empty";
  }
  if (!validateEmail(value)) {
    return "auth.errors.email.invalid";
  }
  return null;
}

export class TeamApi {
  teamData: Team | TeamShort;

  private channel?: Channel;

  constructor(teamData: Team | TeamShort) {
    this.teamData = teamData;
    makeAutoObservable(this);
  }

  async update(body: UpdateTeamBody) {
    const { team } = await updateTeam(this.teamData.permalink, body);
    return { team };
  }

  async uploadAvatar(body: UploadTeamAvatarBody) {
    const { team } = await uploadTeamAvatar(this.teamData.permalink, body);
    return { team };
  }

  async leave() {
    return leaveTeam(this.teamData.permalink);
  }

  subscribe() {
    this.unsubscribe();
    this.channel = subscribe("TeamChannel", { team_id: this.teamData.uid });
  }

  unsubscribe() {
    this.channel?.disconnect();
    this.channel = undefined;
  }
}
